import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { FORGOT_PASSWORD } from '../graphql';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';

export default function ForgotPasswordPage() {
  const [email, setEmail]   = useState('');
  const [sent, setSent]     = useState(false);
  const [error, setError]   = useState('');
  const [forgotPassword, { loading }] = useMutation(FORGOT_PASSWORD);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await forgotPassword({ variables: { email } });
      setSent(true);
    } catch (err) {
      const msg = err.message || '';
      if (msg.includes('status code') || msg.includes('fetch')) {
        setError('Cannot reach server. Please try again in a moment.');
      } else {
        setError(msg || 'Something went wrong.');
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-surface-900 px-6">
      <div className="w-full max-w-[360px]">

        {/* Back link */}
        <Link to="/login"
          className="inline-flex items-center gap-1.5 text-sm text-gray-400 dark:text-white/40 hover:text-gray-700 dark:hover:text-white/70 transition-colors mb-10">
          <ArrowLeft size={14} /> Back to sign in
        </Link>

        {sent ? (
          /* ── Success state ── */
          <div className="text-center py-4">
            <div className="w-14 h-14 rounded-lg flex items-center justify-center mx-auto mb-5"
              style={{ background: '#1a73e8' }}>
              <CheckCircle size={26} className="text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Check your email</h2>
            <p className="text-gray-400 dark:text-white/40 text-sm leading-relaxed">
              We sent a password reset link to<br />
              <span className="text-gray-700 dark:text-white/70 font-semibold">{email}</span>
            </p>
            <p className="text-gray-300 dark:text-white/25 text-xs mt-4">
              Didn't get it? Check your spam folder, or{' '}
              <button onClick={() => setSent(false)}
                className="text-brand-600 dark:text-brand-400 hover:underline font-medium">
                try again
              </button>
            </p>
          </div>
        ) : (
          /* ── Form state ── */
          <>
            <div className="mb-8">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-5 bg-brand-50 dark:bg-brand-900/40 border border-brand-100 dark:border-brand-800">
                <Mail size={20} className="text-brand-600 dark:text-brand-400" />
              </div>
              <p className="text-xs font-semibold text-brand-600 dark:text-brand-400 uppercase tracking-widest mb-2">
                Password reset
              </p>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Forgot your password?</h2>
              <p className="text-gray-400 dark:text-white/40 mt-1.5 text-sm">
                Enter your email and we'll send you a reset link.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-white/50 uppercase tracking-wide mb-1.5">
                  Email address
                </label>
                <input
                  className="input"
                  type="email"
                  placeholder="you@college.edu"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
              </div>

              {error && (
                <div className="flex items-start gap-2.5 px-4 py-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/40">
                  <div className="w-4 h-4 rounded-full bg-red-100 dark:bg-red-800/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-red-500 text-[10px] font-black">!</span>
                  </div>
                  <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full h-11 rounded-lg font-semibold text-white text-sm flex items-center justify-center gap-2 bg-brand-500 hover:bg-brand-600 transition-colors active:scale-[0.98] disabled:opacity-50"
              >
                {loading
                  ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  : 'Send reset link'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
